/*
 * Copyright Higharc Inc. 2019
 */
import {
  Color,
  CubeCamera,
  FloatType,
  LinearFilter,
  RGBAFormat,
  UnsignedByteType,
  Vector3,
  WebGLCubeRenderTarget,
} from "three";
import { BitmapUtil } from "./BitmapUtil";

let cubeCamera: any;
let cubeCameraNear: any;
let cubeCameraFar: any;
let cubeCameraResolution: any;
const originalClearColor = new Color();
let cubeCameraColorSpace: any;
let cubeCameraFramebufferDataType: any;

// Reference:
// http://www.rorydriscoll.com/2012/01/15/cubemap-texel-solid-angle/
// Computes the solid angle covered by a rectangular section of a cube map face. The section is limited by the center
// point of the cube map face and the point x, y given in the range -1 to 1.
function cubeMapRectSolidAngle(x: number, y: number): number {
  return Math.atan2(x * y, Math.sqrt(x * x + y * y + 1));
}

export const CubeCameraUtil = {
  renderCubeMap: function (
    renderer,
    scene,
    positionObj,
    near,
    far,
    cubeResolution,
    colorSpace,
    framebufferDataType,
    clearColor,
    clearAlpha,
    layers,
  ) {
    near = near !== undefined ? near : 0.1;
    far = far !== undefined ? far : 10.0;
    cubeResolution = cubeResolution !== undefined ? cubeResolution : 32;
    if (
      cubeCamera === undefined ||
      cubeCameraNear !== near ||
      cubeCameraFar !== far ||
      cubeCameraResolution !== cubeResolution ||
      cubeCameraColorSpace !== colorSpace ||
      cubeCameraFramebufferDataType !== framebufferDataType
    ) {
      if (cubeCamera !== undefined) {
        // TODO: It's a bit hard to remember to do this. Maybe contribute a fix upstream that adds a dispose()
        // call directly to CubeCamera.
        cubeCamera.renderTarget.dispose();
      }
      const cubeRenderTarget = new WebGLCubeRenderTarget(cubeResolution, {
        format: RGBAFormat,
        magFilter: LinearFilter,
        minFilter: LinearFilter,
        type: framebufferDataType,
      });
      cubeRenderTarget.texture.colorSpace = colorSpace;
      cubeCamera = new CubeCamera(near, far, cubeRenderTarget);
      cubeCameraNear = near;
      cubeCameraFar = far;
      cubeCameraResolution = cubeResolution;
      cubeCameraColorSpace = colorSpace;
      cubeCameraFramebufferDataType = framebufferDataType;
    }
    cubeCamera.traverse(function (obj) {
      if (layers !== undefined) {
        obj.layers.mask = layers.mask;
      } else {
        obj.layers.enableAll();
      }
    });
    scene.add(cubeCamera);
    positionObj.getWorldPosition(cubeCamera.position);
    positionObj.getWorldQuaternion(cubeCamera.quaternion);
    cubeCamera.updateMatrixWorld();

    // TODO: Could be useful to be able to do a postprocessing pass in here.

    renderer.getClearColor(originalClearColor);
    const originalClearAlpha = renderer.getClearAlpha();

    renderer.setClearColor(clearColor, clearAlpha);
    cubeCamera.renderTarget.clear(renderer);

    cubeCamera.update(renderer, scene);

    renderer.setClearColor(originalClearColor);
    renderer.setClearAlpha(originalClearAlpha);

    scene.remove(cubeCamera);

    return cubeCamera.renderTarget;
  },

  readCubeFaces: function (renderer, renderTarget, cubeResolution, processFaceBuffer) {
    const arrayConstructor = CubeCameraUtil.readArrayConstructorFromRenderTarget(renderTarget);
    const readBuffer = new arrayConstructor(cubeResolution * cubeResolution * 4);
    const cubeFaces: any[] = [];
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      renderer.setRenderTarget(renderTarget, faceIndex);
      renderer.readRenderTargetPixels(renderTarget, 0, 0, cubeResolution, cubeResolution, readBuffer, faceIndex);

      cubeFaces.push(processFaceBuffer(readBuffer, renderTarget.texture.colorSpace));
    }
    renderer.setRenderTarget(null);
    return cubeFaces;
  },

  // Generates a cache of cube map texel solid angles.
  // Indexed by intY * cubeResolution + intX.
  generateSolidAngleCache: function (cubeResolution) {
    const cache = new Float32Array(cubeResolution * cubeResolution);
    const texelHalfWidth = 1.0 / cubeResolution;
    let texelCenterY = 1.0 - texelHalfWidth;
    for (let intY = 0; intY < cubeResolution; ++intY) {
      let texelCenterX = -1.0 + texelHalfWidth;
      for (let intX = 0; intX < cubeResolution; ++intX) {
        const texelSolidAngle = CubeCameraUtil.cubeMapTexelSolidAngle(texelCenterX, texelCenterY, texelHalfWidth);
        cache[intY * cubeResolution + intX] = texelSolidAngle;

        texelCenterX += texelHalfWidth * 2.0;
      }
      texelCenterY -= texelHalfWidth * 2.0;
    }
    return cache;
  },

  // Get the solid angle covered by a single cube map texel.
  // Here centerX and centerY are coordinates of the texel center on the cube map face. They range from -1 to 1.
  // texelHalfWidth is half of the width of one texel in the same coordinates.
  cubeMapTexelSolidAngle: function (centerX, centerY, texelHalfWidth) {
    const x0 = centerX - texelHalfWidth;
    const y0 = centerY - texelHalfWidth;
    const x1 = centerX + texelHalfWidth;
    const y1 = centerY + texelHalfWidth;
    return (
      cubeMapRectSolidAngle(x0, y0) +
      cubeMapRectSolidAngle(x1, y1) -
      cubeMapRectSolidAngle(x0, y1) -
      cubeMapRectSolidAngle(x1, y0)
    );
  },

  // Generates a cache of texel direction vectors.
  // Indexed by (faceIndex * cubeResolution + intY) * cubeResolution + intX.
  generateTexelDirectionCache: function (cubeResolution) {
    const cache: Vector3[] = [];
    const texelHalfWidth = 1.0 / cubeResolution;
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      let texelCenterY = 1.0 - texelHalfWidth;
      for (let intY = 0; intY < cubeResolution; ++intY) {
        let texelCenterX = -1.0 + texelHalfWidth;
        for (let intX = 0; intX < cubeResolution; ++intX) {
          const texelDirection = new Vector3();
          CubeCameraUtil.getCubeMapTexelDirection(faceIndex, texelCenterX, texelCenterY, texelDirection);
          cache.push(texelDirection);

          texelCenterX += texelHalfWidth * 2.0;
        }
        texelCenterY -= texelHalfWidth * 2.0;
      }
    }
    return cache;
  },

  // Writes direction of the given cube map texel into Vector3 "direction".
  // The face index should be according to the order +x, -x, +y, -y, +z, -z.
  // texelCenterX and texelCenterY are texel coordinates inside the cube map face ranging from -1.0 to 1.0.
  getCubeMapTexelDirection: function (faceIndex, texelCenterX, texelCenterY, direction) {
    switch (faceIndex) {
      case 0: // Positive x face
        direction.z = -texelCenterX;
        direction.y = texelCenterY;
        direction.x = 1;
        break;
      case 1: // Negative x face
        direction.z = texelCenterX;
        direction.y = texelCenterY;
        direction.x = -1;
        break;
      case 2: // Positive y face
        direction.x = texelCenterX;
        direction.z = -texelCenterY;
        direction.y = 1;
        break;
      case 3: // Negative y face
        direction.x = texelCenterX;
        direction.z = texelCenterY;
        direction.y = -1;
        break;
      case 4: // Positive z face
        direction.x = texelCenterX;
        direction.y = texelCenterY;
        direction.z = 1;
        break;
      case 5: // Negative z face
        direction.x = -texelCenterX;
        direction.y = texelCenterY;
        direction.z = -1;
        break;
    }
    direction.normalize();
  },

  // Returns the cube faces downsampled to half resolution.
  // Assumes that the cube map resolution is divisible by two.
  linearDownsampleCubemap: function (cubeFaces, originalCubeResolution, targetCubeResolution, numChannels) {
    const downsampledFaces: any[] = [];
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      downsampledFaces.push(
        BitmapUtil.linearDownsampleBitmap(
          cubeFaces[faceIndex],
          originalCubeResolution,
          originalCubeResolution,
          targetCubeResolution,
          targetCubeResolution,
          numChannels,
        ),
      );
    }
    return downsampledFaces;
  },

  readArrayConstructorFromRenderTarget: function (renderTarget) {
    if (renderTarget.texture.type === UnsignedByteType) {
      return Uint8Array;
    } else if (renderTarget.texture.type === FloatType) {
      return Float32Array;
    }
    console.error("Unrecognized render target type when determining readback typed array constructor");
    return Uint8Array;
  },

  readPixelsTypeFromRenderTarget: function (renderer, renderTarget) {
    if (renderTarget.texture.type === UnsignedByteType) {
      return renderer.getContext().UNSIGNED_BYTE;
    } else if (renderTarget.texture.type === FloatType) {
      return renderer.getContext().FLOAT;
    }
    console.error("Unrecognized render target type when determining readback type");
    return renderer.getContext().UNSIGNED_BYTE;
  },
};
